import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Task, TaskEvent } from '@/api';
import { useWebsocket } from '@/common/providers/websocket-provider';
import { addOrUpdateTaskEventsCache } from './tasks-cache';

/**
 * Keeps `useFetchTaskEvents` fresh: subscribes to the company's task events
 * and writes the ones belonging to this task into its cached events list.
 */
export function useEnableTaskEventsSockets(taskId: string, companyId: string) {
    const queryClient = useQueryClient();

    const { subscribe, unsubscribe, on, off } = useWebsocket();
    useEffect(() => {
        if (companyId) {
            const ids = subscribe([
                { resource: `companies#${companyId}.tasks_events` }
            ]);
            return () => {
                unsubscribe(ids);
            };
        }
    }, [companyId, subscribe, unsubscribe]);

    useEffect(() => {
        const tasksCallback = (message: unknown) => {
            const {
                data: { task, taskEvents }
            } = message as {
                data: { task: Task; taskEvents: TaskEvent[] };
            };

            if (task.id === taskId) {
                addOrUpdateTaskEventsCache(queryClient, taskId, taskEvents);
            }
        };

        on('tasks_events_v3', tasksCallback);

        return () => {
            off('tasks_events_v3', tasksCallback);
        };
    }, [on, off, queryClient, taskId]);
}
